<script setup lang="ts">
// icons
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

// validation
import { useField } from 'vee-validate';

// components
import InputTextarea from '@/components/form/InputTextarea.vue';

export interface CiImageUploadElementProps {
  disabled?: boolean;
  errors?: any;
  rules?: string;
  value: any;
}
const props = withDefaults(defineProps<CiImageUploadElementProps>(), {
  disabled: false,
  errors: {},
  rules: '',
});

const emit = defineEmits<{
  input: [value: any];
  remove: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const attrs = useAttrs();
const { $gettext } = useGettext();
const { filesizeHumanReadable } = useFileSize();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const { value } = useField(attrs.name, props.rules);
value.value = props.value;

const { value: descriptionValue } = useField<string>(`description_${attrs.id}`);

const hasErrors = computed(() => {
  return props.errors?.messages?.length > 0 || props.value?.errors?.length > 0;
});

const isTooLarge = computed(() => {
  return hasError('image_too_large');
});

const errorTexts = computed(() => {
  return props.value?.errors?.map((error) => {
    if (error.code && error.hideCode) {
      return error.message;
    } else {
      return `${error.code}: ${error.message}`;
    }
  });
});

/********************
 * FUNCTIONS         *
 ********************/
function hasError(code) {
  if (!hasErrors.value) {
    return false;
  }
  return props.value.errors.filter(err => err.code === code).length > 0;
}

watch(
  () => descriptionValue.value,
  (newDescription) => {
    emit('input', { ...props.value, description: newDescription });
  },
  { immediate: false },
);
</script>

<template>
  <div
    :class="[hasErrors ? 'border-danger' : 'border-primary', { 'opacity-50': props.disabled }]"
    class="mb-6 border border-dashed p-6"
  >
    <div class="row">
      <div class="col-12 col-md-4 col-lg-3">
        <div class="relative w-full pb-[75%]">
          <img
            class="absolute size-full rounded-DEFAULT object-cover"
            :src="props.value.src"
            :alt="props.value.name"
          />
        </div>
        <div class="px-0 py-4">
          <div class="truncate font-bold">
            {{ props.value.name }}
          </div>
          <div
            :class="{ 'has-error text-danger': isTooLarge }"
            class="truncate"
          >
            <span>{{ $gettext('Size:') }}</span> {{ filesizeHumanReadable(props.value.size, locale) }}
          </div>
        </div>
      </div>
      <div class="col-12 col-md-7 col-lg-8">
        <InputTextarea
          :id="`description_${attrs.id}`"
          v-model="descriptionValue"
          :disabled="props.disabled"
          :label="$gettext('Description')"
          :placeholder="$gettext('Describe the image here (max. 1000 characters)')"
          :rows="5"
          inverted
          :name="`description_${attrs.id}`"
        />
      </div>
      <div class="col-12 col-md-1 flex items-center justify-center">
        <div class="mb-6 pr-4">
          <CiButton
            class="button button--circle"
            :icon="faTrashAlt"
            icon-ratio="1"
            :disabled="props.disabled"
            :icon-class="{ 'fill-danger': true, 'fill-white': props.disabled }"
            @click="emit('remove')"
          />
        </div>
      </div>
    </div>
    <div
      v-if="hasErrors"
      class="row"
    >
      <div class="col-12 has-error text-danger">
        <CiInfoPanel
          v-for="(errorText, index) in errorTexts"
          :key="`error-${index}`"
          :text="errorText"
          :condition="errorText !== null"
          type="danger"
        />
      </div>
    </div>
  </div>
</template>

<style></style>
